// Recent runs: what was owed, what was captured, and what was refused.
//
// THE REFUSALS ARE THE POINT OF THIS PAGE. A reading the gate rejected is stored,
// with its reason, and the obligation it was meant to satisfy stays OWED. That is
// the whole argument: a refusal that is invisible is a slower silent loss than no
// gate at all — the number was taken, the person believes it was recorded, and
// nothing anywhere says otherwise.
//
// So there is deliberately NO filter on this list. Not a "hide rejected" toggle,
// not a default that shows accepted readings only, not a tab. The one control that
// would make this screen comfortable is the one that would make it useless.
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { VerdictBadge } from '../../components/VerdictBadge';
import { PilotBadge } from '../../components/Pilot';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/DataTable';
import { rows, useResource } from '../../shells/OfficeShell';
import { api } from '../../lib/api';
import { DOOR_LABEL, RUN_STATUS_LABEL, rejectText } from '../../lib/vocabulary';
// The "no check for more than a shift" test (rule 17), shared with the operator line picker (UI/UX
// audit, 31 Oct 2026: one copy of the rule; LinePick's private one is gone).
import { quietLongerThanAShift } from '../../lib/runQuiet';
import { ReadingPhotosButton, type ReadingPhoto, type ReferencePhoto } from './LinePhotos';

/** GET /runs — a flat row per run. `specVersion` is the version NUMBER (from
 *  `sv.Version AS SpecVersion`), not an object and not the row id: printing the
 *  id as "v41" is a different claim from "v3". There is no `lineName` in this
 *  projection, only the code. */
interface RunSummary {
  id: number;
  lineCode?: string | null;
  productName?: string | null;
  productCode?: string | null;
  d365BatchNumber?: string | null;
  startedAt: string;
  endedAt: string | null;
  status: 'OPEN' | 'CLOSED';
  specVersionId: number;
  specVersion?: number | null;
  /** Started in pilot mode (rule 27): not a controlled record, for ever. */
  isPilot?: boolean;
  /** The newest reading's capture time (`MAX(CapturedAtDevice)`), null when nothing was captured.
   *  GET /runs has sent it since the operator line picker needed it; UX-36 reads it here too. */
  lastReadingAt?: string | null;
}

/** "No check for 11 h" under two days, "No check for 3 d" beyond. */
export function quietLabel(ms: number): string {
  const hours = Math.floor(ms / 3_600_000);
  return hours < 48 ? `No check for ${hours} h` : `No check for ${Math.floor(hours / 24)} d`;
}

/**
 * GET /runs/{id} — quality_run_status, an ENVELOPE. `obligations` is FOUR COUNTS,
 * and they are what the progress strip is drawn from.
 *
 * ⚠ Not GET /runs/{id}/obligations. That route lists obligations and its status
 * filter DEFAULTS TO OWED, so counting statuses off its rows reports met, missed
 * and awaiting-result as zero on every run — a progress bar that says nothing has
 * been done on a run that is finished.
 */
interface RunStatusPayload {
  obligations?: {
    owed: number;
    awaitingResult: number;
    awaitingVerify: number;
    met: number;
    missed: number;
    waived: number;
    total: number;
  };
  /** Slice 1b. Whether the LINE was ever released to run at all — a different
   *  question from whether the product is in spec, and the one an auditor asks
   *  first. Optional, because an older payload does not carry it. */
  readiness?: { total: number; outstanding: number; released: boolean; hasForm: boolean };
  /** Rule 27: whether this run is a controlled record at all. */
  pilot?: { isPilot: boolean; acceptsNewEntries: boolean; note: string | null };
  /** The run itself, as the status read sees it now (the list may be older). */
  run?: { status?: 'OPEN' | 'CLOSED' };
}

/** GET /runs/{id}/obligations?status=OWED — quality_owed_checks, an ENVELOPE with
 *  the rows under `obligations`. Used for the list of what is STILL owed, which is
 *  the question the office asks of an open run. */
interface ObligationsPayload {
  obligations?: Obligation[];
}

/**
 * ⚠ THREE KINDS (UI/UX audit, 31 Oct 2026, UX-34). quality_owed_checks has sent READINESS - a
 * start-up check of the line, slice 1b - since 17 Sep, and this type left it out, so the chip's
 * `FIRST_OFF ? 'first off' : 'adherence'` named all 32 start-up checks of a run "adherence". The
 * words are the plant's: start-up (the line, before product), first off, in-process (recurring).
 */
type ObligationKind = 'FIRST_OFF' | 'ADHERENCE' | 'READINESS';
const KIND_LABEL: Record<ObligationKind, string> = { READINESS: 'start-up', FIRST_OFF: 'first off', ADHERENCE: 'in-process' };

interface Obligation {
  obligationId: number;
  kind: ObligationKind;
  dueAt: string;
  status: 'OWED' | 'AWAITING_RESULT' | 'MET' | 'MISSED';
  overdue: boolean;
  missedReason: string | null;
  parameter: { id: number; code: string; name: string };
}

/** GET /runs/{runId}/readings — a bare array, rejected rows included. `id` is a
 *  BIGINT and arrives as a string. */
interface Reading {
  id: number | string;
  specParameterId: number;
  parameterName?: string | null;
  parameterCode?: string | null;
  capturedByUpn: string;
  capturedAtDevice: string;
  receivedAtServer: string;
  captureMode: 'ONLINE' | 'QUEUED';
  clientKind: 'SPA' | 'GATEWAY';
  ingestVerdict: 'ACCEPTED' | 'REJECTED';
  rejectReason: string | null;
  verdict: 'IN' | 'ACTION' | 'OUT' | null;
  aggregate: number | null;
  /** The parameter's kind (added to the payload, UI/UX audit 31 Oct 2026, UX-18). Absent from an
   *  older API: then the number is shown as before. */
  valueKind?: 'NUMERIC' | 'ATTRIBUTE' | null;
  evidenceRef: string | null;
  /** On a pilot run (rule 27). */
  isPilot?: boolean;
  verificationId?: number | string | null;
  verifiedByUpn?: string | null;
  verifyOutcome?: 'PASS' | 'FAIL' | null;
  verificationIsPilot?: boolean | null;
  /** Actual photos (migration 034), ids only. Null or absent = not sent: not known (rule 15). */
  photos?: ReadingPhoto[] | null;
}

type ReferenceLookup = (specParameterId: number) => Promise<{ parameterId: number; photos: ReferencePhoto[] } | null>;

/**
 * A check's reference photos, from what the run already serves: the product spec version it is
 * judged against (`GET /spec-versions/{id}`) and the line's start-up items (`GET /runs/{id}/readiness`).
 * Read once per opened run, only when a photo viewer asks. A parameter neither names resolves to
 * null (actual photos only); both reads failing rejects, so the viewer says "could not be read".
 */
function referenceLookup(runId: number, specVersionId: number): ReferenceLookup {
  type Params = Array<{ id?: number; referencePhotos?: ReferencePhoto[] | null }>;
  let loaded: Promise<Map<number, ReferencePhoto[]>> | null = null;
  const load = async () => {
    const [spec, ready] = await Promise.allSettled([
      api.get<{ parameters?: Params }>(`/spec-versions/${specVersionId}`),
      api.get<{ items?: Array<{ parameter?: Params[number] }> }>(`/runs/${runId}/readiness`),
    ]);
    if (spec.status === 'rejected' && ready.status === 'rejected') throw spec.reason;
    const out = new Map<number, ReferencePhoto[]>();
    const add = (p: Params[number] | undefined) => {
      if (p && typeof p.id === 'number' && Array.isArray(p.referencePhotos)) out.set(p.id, p.referencePhotos);
    };
    if (spec.status === 'fulfilled') (spec.value?.parameters ?? []).forEach(add);
    if (ready.status === 'fulfilled') (ready.value?.items ?? []).forEach((i) => add(i.parameter));
    return out;
  };
  return async (specParameterId) => {
    loaded ??= load().catch((err) => {
      loaded = null; // a failed read is asked again on Try again, never cached as "none"
      throw err;
    });
    const photos = (await loaded).get(specParameterId);
    return photos ? { parameterId: specParameterId, photos } : null;
  };
}

function stamp(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
}

const rcol = createColumnHelper<RunSummary>();

/** The run list. Rebuilt when the open run changes, for its Open/Close button. */
function runColumns(selected: number | null, toggle: (id: number) => void) {
  return [
    rcol.accessor('id', {
      header: 'Run',
      cell: (c) => (
        <span className="inline-flex items-center gap-(--s2)">
          {c.getValue()}
          <PilotBadge isPilot={c.row.original.isPilot} />
        </span>
      ),
      meta: { rowHeader: true, mono: true, nowrap: true },
    }),
    rcol.accessor((r) => r.productName ?? r.productCode ?? '', {
      id: 'product',
      header: 'Product',
      cell: (c) => c.getValue() || '—',
    }),
    rcol.accessor((r) => r.lineCode ?? '', { id: 'line', header: 'Line', cell: (c) => c.getValue() || '—' }),
    rcol.accessor((r) => r.d365BatchNumber ?? '', {
      id: 'batch',
      header: 'Batch',
      cell: (c) => c.getValue() || '—',
      meta: { mono: true, nowrap: true },
    }),
    rcol.accessor((r) => (r.specVersion ? `v${r.specVersion}` : `id ${r.specVersionId}`), {
      id: 'spec',
      header: 'Spec',
      sortingFn: (a, b) => (a.original.specVersion ?? -1) - (b.original.specVersion ?? -1),
      meta: { mono: true, nowrap: true },
    }),
    rcol.accessor('startedAt', { header: 'Started', cell: (c) => stamp(c.getValue()), meta: { nowrap: true } }),
    rcol.accessor((r) => r.endedAt ?? '', {
      id: 'ended',
      header: 'Ended',
      cell: (c) => stamp(c.row.original.endedAt),
      meta: { nowrap: true },
    }),
    // UX-36 (UI/UX audit, 31 Oct 2026; rule 17): an OPEN run nobody has checked for more than a
    // shift carries a muted chip beside its status - a question for the office, never a verdict.
    // Muted, not amber: the run may simply have stopped and not been closed yet.
    rcol.accessor('status', {
      header: 'Status',
      cell: (c) => {
        const quiet = quietLongerThanAShift(c.row.original);
        return (
          <span className="inline-flex flex-wrap items-center gap-(--s2)">
            {/* "Running", not OPEN (UI/UX audit, 31 Oct 2026, UX-18). */}
            {RUN_STATUS_LABEL[c.getValue()] ?? c.getValue()}
            {quiet !== null ? (
              <span className="rounded-(--radius-pill) border border-(--border) px-(--s2) text-(length:--fs-xs) font-semibold whitespace-nowrap text-(--muted)">
                {quietLabel(quiet)}
              </span>
            ) : null}
          </span>
        );
      },
    }),
    // UX-45 (UI/UX audit, 31 Oct 2026): the toggle read "Close" on a running run - the word for
    // ending one - and every row's was just "Open", so a screen reader heard 50 identical buttons.
    // It shows the run's readings below; it says so, and its name carries the run (label in name).
    rcol.display({
      id: 'open',
      header: () => <span className="sr-only">Readings</span>,
      cell: (c) => {
        const id = c.row.original.id;
        const shown = selected === id;
        return (
          <Button
            variant="outline"
            type="button"
            aria-expanded={shown}
            aria-label={`${shown ? 'Hide' : 'Show'} readings for run ${id}`}
            onClick={() => toggle(id)}
          >
            {shown ? 'Hide readings' : 'Show readings'}
          </Button>
        );
      },
    }),
  ];
}

const dcol = createColumnHelper<Reading>();
const checkName = (r: Reading) => r.parameterName ?? r.parameterCode ?? `Parameter ${r.specParameterId}`;
/** The readings table. Rebuilt per opened run, for its reference-photo lookup. */
function readingColumns(lookup: ReferenceLookup) {
  return [
    // Order (UI/UX audit, 31 Oct 2026, UX-15): Check first and pinned (stickyFirst), then Verdict
    // and Reject reason - the refusals are the point of this page, and at iPad width they sat
    // past the fold. By stays on screen (rule 2: who took it is the record). Mode and Door last.
    dcol.accessor((r) => r.parameterName ?? r.parameterCode ?? `Parameter ${r.specParameterId}`, {
      id: 'check',
      header: 'Check',
      cell: (c) => (
        <span className="inline-flex items-center gap-(--s2)">
          {c.getValue()}
          <PilotBadge isPilot={c.row.original.isPilot} />
        </span>
      ),
      meta: { rowHeader: true },
    }),
    // REJECTED goes through the same badge as a verdict, which draws it dashed. It
    // is not a fourth verdict — the gate refused the reading, so nothing was ever
    // judged — and the dashed border is what keeps it distinguishable from OUT at
    // a glance while both stay red.
    dcol.accessor((r) => (r.ingestVerdict === 'REJECTED' ? 'REJECTED' : (r.verdict ?? '')), {
      id: 'verdict',
      header: 'Verdict',
      cell: (c) => {
        const r = c.row.original;
        return <VerdictBadge verdict={r.ingestVerdict === 'REJECTED' ? 'REJECTED' : r.verdict} />;
      },
    }),
    // The reason, in full and in the row. A rejected reading with its reason
    // hidden behind a click is a rejected reading nobody reads.
    // The stored reason is "code: sentence"; the sentence is shown and the code kept in the tooltip
    // (UI/UX audit, 31 Oct 2026, UX-18: the row read "evidence_required: …").
    dcol.accessor((r) => rejectText(r.rejectReason).sentence, {
      id: 'reason',
      header: 'Reject reason',
      cell: (c) => {
        const code = rejectText(c.row.original.rejectReason).code ?? undefined;
        return c.row.original.ingestVerdict === 'REJECTED' ? (
          <span className="font-semibold text-(--bad)" title={code}>{c.getValue()}</span>
        ) : (
          <span title={code}>{c.getValue()}</span>
        );
      },
    }),
    // Not sortable: results of different checks are in different units. A Yes/No check stores
    // 1 or 0; it reads Yes or No (UX-18), from the parameter's valueKind on the readings payload.
    dcol.accessor((r) => (r.aggregate == null ? '—' : r.valueKind === 'ATTRIBUTE' ? (r.aggregate === 1 ? 'Yes' : 'No') : r.aggregate), {
      id: 'result',
      header: 'Result',
      enableSorting: false,
      meta: { align: 'right', mono: true },
    }),
    dcol.accessor('capturedByUpn', { header: 'By' }),
    dcol.accessor('capturedAtDevice', { header: 'Taken', cell: (c) => stamp(c.getValue()), meta: { nowrap: true } }),
    // The second signature, with its own PILOT mark (a verification is pilot when its reading is).
    dcol.accessor((r) => (r.verificationId ? `${r.verifyOutcome ?? ''} ${r.verifiedByUpn ?? ''}` : ''), {
      id: 'verified',
      header: 'Verified',
      cell: (c) => {
        const r = c.row.original;
        if (!r.verificationId) return '—';
        return (
          <span className="inline-flex items-center gap-(--s2)">
            {r.verifyOutcome} · {r.verifiedByUpn}
            <PilotBadge isPilot={r.verificationIsPilot} />
          </span>
        );
      },
    }),
    // Actual photos (migration 034): a camera count, the viewer on click. A REJECTED reading keeps
    // its photo and shows it the same way (rule 4).
    dcol.accessor((r) => (Array.isArray(r.photos) ? r.photos.length : -1), {
      id: 'photos',
      header: 'Photos',
      enableSorting: false,
      cell: (c) => {
        const r = c.row.original;
        return <ReadingPhotosButton readingId={r.id} checkName={checkName(r)} photos={r.photos} specParameterId={r.specParameterId} loadReference={lookup} />;
      },
    }),
    dcol.accessor((r) => (r.captureMode === 'QUEUED' ? 'Queued' : 'Online'), { id: 'mode', header: 'Mode' }),
    // Which door the reading came through. A gateway capture is a real user
    // action, and the record says which it was.
    dcol.accessor((r) => DOOR_LABEL[r.clientKind] ?? r.clientKind, { id: 'door', header: 'Door' }),
  ];
}

/**
 * Every status quality_run_status counts, in the order work moves through them.
 *
 * ⚠ SIX, not four (UI/UX audit, 31 Oct 2026, UX-04). The strip and the counts line drew MET,
 * AWAITING_RESULT, OWED and MISSED only, and dropped the awaitingVerify and waived the API has sent
 * since slice 1b. Start-up checks answered and waiting for a second person (rule 9) are
 * AWAITING_VERIFY, so a run with ~70 accepted start-up readings read "0 met · 0 owed · 5 missed" -
 * a record saying nothing was done on a line where everything was done and nobody had signed it -
 * and a banner saying "31 of 31 outstanding" sat over 30 owed chips: the 31st was awaiting verify.
 * One list feeds the strip and the line, so a status cannot be in one and missing from the other.
 *
 * Existing tokens only, one colour each: the two "someone still has to act" states share the amber
 * family (OWED the line amber, AWAITING_VERIFY the deeper text amber); WAIVED - released by
 * concession, nothing owed - is neutral, darker than AWAITING_RESULT so the two greys separate.
 */
type CountKey = 'MET' | 'WAIVED' | 'AWAITING_VERIFY' | 'AWAITING_RESULT' | 'OWED' | 'MISSED';
const SEGMENTS: Array<{ key: CountKey; label: string; fill: string }> = [
  { key: 'MET', label: 'met', fill: 'var(--ok-line)' },
  { key: 'WAIVED', label: 'waived', fill: 'var(--muted)' },
  { key: 'AWAITING_VERIFY', label: 'awaiting verify', fill: 'var(--warn)' },
  { key: 'AWAITING_RESULT', label: 'awaiting result', fill: 'var(--border-strong)' },
  { key: 'OWED', label: 'owed', fill: 'var(--warn-line)' },
  { key: 'MISSED', label: 'missed', fill: 'var(--bad-line)' },
];

/** Obligation progress as one strip. Geometry in SVG attributes, colours as tokens. */
function ProgressStrip({ counts }: { counts: Record<CountKey, number> }) {
  const total = Object.values(counts).reduce((s, n) => s + n, 0);
  if (total === 0) return null;
  let x = 0;
  return (
    <div className="overflow-hidden rounded-(--radius-sm)">
      <svg
        viewBox="0 0 100 8"
        preserveAspectRatio="none"
        role="img"
        aria-label={SEGMENTS.map((g) => `${counts[g.key]} ${g.label}`).join(', ')}
        className="block h-[14px] w-full"
      >
        <rect x="0" y="0" width="100" height="8" fill="var(--subtle)" />
        {SEGMENTS.map(({ key, fill }) => {
          const w = (counts[key] / total) * 100;
          const rect = <rect key={key} x={x} y="0" width={w} height="8" fill={fill} />;
          x += w;
          return rect;
        })}
      </svg>
    </div>
  );
}

/** The counts line. Each count sits beside a swatch of its strip colour: the line is the strip's key. */
function CountsLine({ counts }: { counts: Record<CountKey, number> }) {
  return (
    <ul
      aria-label="Obligations on this run"
      className="m-0 mt-(--s1) flex list-none flex-wrap gap-x-(--s3) gap-y-(--s1) p-0 text-(length:--fs-sm) text-(--muted)"
    >
      {SEGMENTS.map(({ key, label, fill }) => (
        <li key={key} className="inline-flex items-center gap-(--s1)">
          <span aria-hidden="true" className="inline-block h-[10px] w-[10px] rounded-[2px]" style={{ background: fill }} />
          {counts[key]} {label}
        </li>
      ))}
    </ul>
  );
}

/** The readiness sentence (UX-35): present tense while the run is open, past once it has closed. */
export function readinessSentence(
  r: { total: number; outstanding: number; released: boolean; hasForm: boolean },
  waived: number,
  open: boolean,
): string {
  if (!r.hasForm) {
    return `This line ${open ? 'has' : 'had'} no approved start-up form, so nothing was asked before it ran. That is not the same as a line that passed.`;
  }
  if (r.released) {
    return `Line ${open ? 'released' : 'was released'} — all ${r.total} start-up checks met or waived${waived > 0 ? `, ${waived} by concession` : ''}.`;
  }
  return open
    ? `Line NOT released — ${r.outstanding} of ${r.total} start-up checks outstanding. Product checks are being refused on this run.`
    : `Line was NOT released — ${r.outstanding} of ${r.total} start-up checks were not met. Product checks were refused on this run.`;
}

/** One owed check as a chip: named, not counted, with its kind in the plant's words. */
function OwedChip({ o }: { o: Obligation }) {
  return (
    <li
      className={`rounded-(--radius-pill) border px-(--s3) py-(--s1) text-(length:--fs-xs) font-semibold ${
        o.overdue ? 'border-(--warn-line) bg-(--warn-bg) text-(--warn)' : 'border-(--border) text-(--muted)'
      }`}
    >
      {o.parameter.name} · {KIND_LABEL[o.kind] ?? o.kind} · due {stamp(o.dueAt)}
      {o.overdue ? ' · overdue' : ''}
    </li>
  );
}

export function Runs() {
  const runs = useResource<RunSummary[]>('/runs?limit=50');
  const [selected, setSelected] = useState<number | null>(null);
  const toggle = useCallback((id: number) => setSelected((cur) => (cur === id ? null : id)), []);
  const columns = useMemo(() => runColumns(selected, toggle), [selected, toggle]);

  const status = useResource<RunStatusPayload>(selected ? `/runs/${selected}` : null);
  const obligations = useResource<ObligationsPayload>(
    selected ? `/runs/${selected}/obligations?status=OWED` : null,
  );
  const readings = useResource<Reading[]>(selected ? `/runs/${selected}/readings` : null);

  const runList = rows<RunSummary>(runs.data);
  const selectedSpecVersionId = runList.find((r) => r.id === selected)?.specVersionId ?? null;
  const readingCols = useMemo(
    () =>
      readingColumns(
        selected !== null && selectedSpecVersionId !== null ? referenceLookup(selected, selectedSpecVersionId) : async () => null,
      ),
    [selected, selectedSpecVersionId],
  );
  const owed = rows<Obligation>(obligations.data?.obligations);
  const owedStartUp = owed.filter((o) => o.kind === 'READINESS');
  const owedStartUpOverdue = owedStartUp.filter((o) => o.overdue).length;
  const owedProduct = owed.filter((o) => o.kind !== 'READINESS');
  const reads = rows<Reading>(readings.data);

  // Null until the run answers (UI/UX audit, 31 Oct 2026, UX-10; rule 15). This used to zero every
  // count, so a run still being read - or one whose read failed - said "0 met · 0 owed · 0 missed",
  // a claim about the line nobody had made. An absent single count inside a payload that DID arrive
  // is still the zero it means (an older payload without awaitingVerify / waived).
  const tally = status.data?.obligations;
  const counts: Record<CountKey, number> | null = tally
    ? {
        OWED: tally.owed ?? 0,
        AWAITING_RESULT: tally.awaitingResult ?? 0,
        AWAITING_VERIFY: tally.awaitingVerify ?? 0,
        MET: tally.met ?? 0,
        MISSED: tally.missed ?? 0,
        WAIVED: tally.waived ?? 0,
      }
    : null;
  const runOpen = (status.data?.run?.status ?? runList.find((r) => r.id === selected)?.status) === 'OPEN';
  const pilot = status.data?.pilot;
  const readiness = status.data?.readiness;
  const rejected = reads.filter((r) => r.ingestVerdict === 'REJECTED').length;

  return (
    <div className="flex flex-col gap-(--s5)">
      <header>
        <h1 className="m-0 font-display text-[34px] font-semibold leading-none tracking-[-0.01em]">Runs</h1>
        <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">
          Each run is judged against the specification version it started under, not the live one.
        </p>
      </header>

      {/* Capped so an opened run's detail shows below the list rather than a
          screen further down. */}
      <DataTable
        data={runList}
        columns={columns}
        caption="Recent runs"
        getRowId={(r) => String(r.id)}
        isRowSelected={(r) => r.id === selected}
        // The run's id stays beside its Show readings button when the list scrolls sideways (UX-15).
        stickyFirst
        search="Search run, product, line or batch"
        loading={runs.loading}
        error={runs.error}
        onRetry={runs.reload}
        empty="No runs yet."
        maxHeight="26rem"
      />

      {selected ? (
        <section className="flex flex-col gap-(--s4)">
          <div className="rounded-(--radius) border border-(--border) bg-(--card) shadow-card p-(--s4)">
            <h2 className="m-0 flex items-center gap-(--s2) font-display text-[22px] font-semibold leading-tight">
              Run {selected} · checks
              <PilotBadge isPilot={status.data?.pilot?.isPilot} size="lg" />
            </h2>
            {/* Rule 27 (UI/UX audit, 31 Oct 2026, UX-11): no pilot notice on the run screens (Amit:
                no banner 27 Oct, no strip 28 Oct). The PILOT badges on the run, its readings and
                its verifications carry the mark; the one sentence left is the one that changes what
                a person can do - a pilot run whose window has closed takes nothing new. */}
            {pilot?.isPilot && pilot.acceptsNewEntries === false ? (
              <p className="m-0 mt-(--s1) text-(length:--fs-sm) font-semibold text-(--warn)">
                Pilot mode has ended: this run takes no new readings or verifications. Close it.
              </p>
            ) : null}
            {/* Readiness first, because it is the prior question: a run can be
                perfectly up to date on its product checks and have started on a line
                that was never released. Three states, and the third is the one people
                misread — no form at all is NOT a pass.
                UX-35 (UI/UX audit, 31 Oct 2026): it sat above the run list, so opening a run
                pushed the list down under the cursor and the sentence read as the page's, not
                the run's. It is the run's, so it lives in the run's card. And on a CLOSED run
                it is history: "Product checks are being refused" said of a run that ended last
                week is a claim about now that is not true. Same three states, past tense. */}
            {readiness ? (
              <p
                className={`m-0 mt-(--s2) rounded-(--radius) border p-(--s3) text-(length:--fs-sm) font-semibold ${
                  !readiness.hasForm
                    ? 'border-(--warn-line) bg-(--warn-bg) text-(--warn)'
                    : readiness.released
                      ? 'border-(--border-strong) bg-(--subtle)'
                      : 'border-(--bad-line) bg-(--bad-bg) text-(--bad)'
                }`}
              >
                {readinessSentence(readiness, tally?.waived ?? 0, runOpen)}
              </p>
            ) : null}
            {/* Counts only once the run has answered (rule 15): loading says so, and a failed read
                says "not known" with a way to ask again - never a row of zeros. */}
            {counts ? (
              <>
                <CountsLine counts={counts} />
                <div className="mt-(--s2)">
                  <ProgressStrip counts={counts} />
                </div>
              </>
            ) : status.error ? (
              <div role="alert" className="mt-(--s1) flex flex-wrap items-center gap-(--s2) text-(length:--fs-sm)">
                <span className="font-semibold text-(--warn)">Counts not known: {status.error}</span>
                <Button type="button" variant="outline" size="sm" onClick={status.reload}>
                  Try again
                </Button>
              </div>
            ) : (
              <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">Loading counts…</p>
            )}
            {/* UX-04: answered start-up checks wait for a second person, and nothing here led on to
                the screen where that happens. This links to Verify, which still refuses anyone
                verifying their own answer (rule 9): a way in, never a permission. Open runs only - a
                closed run's AWAITING_VERIFY was booked MISSED when it closed (rule 17). */}
            {counts && runOpen && counts.AWAITING_VERIFY > 0 ? (
              <p className="m-0 mt-(--s2) text-(length:--fs-sm) font-semibold">
                {counts.AWAITING_VERIFY} start-up check{counts.AWAITING_VERIFY === 1 ? '' : 's'} answered, waiting for
                a second person.{' '}
                <Link to={`/op/run/${selected}/verify`} className="underline">
                  Verify run {selected}
                </Link>
              </p>
            ) : null}
            {/* An obligation still owed after a rejected reading is the design
                working, not a bug: the check was attempted and it was refused, so
                it is still owed. */}
            {counts && counts.OWED > 0 && rejected > 0 ? (
              <p className="m-0 mt-(--s2) text-(length:--fs-sm) font-semibold text-(--warn)">
                {rejected} reading{rejected === 1 ? '' : 's'} on this run {rejected === 1 ? 'was' : 'were'}{' '}
                refused, so the checks they were meant to satisfy are still owed.
              </p>
            ) : null}

            {/* Named, not counted. "Three owed" is a number; "metal detection,
                due 06:40, overdue" is a check somebody has to go and take. */}
            {/* UX-34 (UI/UX audit, 31 Oct 2026): 32 identical start-up chips buried the two
                product checks a person had to go and take. The start-up checks are one walk-around
                of the line, so they fold into one line that opens to the full list - every one still
                named, none hidden behind a count alone - and the product checks stay one by one. */}
            {owedStartUp.length > 0 ? (
              <details className="mt-(--s3) text-(length:--fs-sm)">
                <summary className={`cursor-pointer font-semibold ${owedStartUpOverdue > 0 ? 'text-(--warn)' : ''}`}>
                  {owedStartUp.length} start-up check{owedStartUp.length === 1 ? '' : 's'} owed
                  {owedStartUpOverdue > 0 ? ` · ${owedStartUpOverdue} overdue` : ''}
                </summary>
                <ul aria-label="Start-up checks owed" className="m-0 mt-(--s2) flex list-none flex-wrap gap-(--s2) p-0">
                  {owedStartUp.map((o) => (
                    <OwedChip key={o.obligationId} o={o} />
                  ))}
                </ul>
              </details>
            ) : null}
            {owedProduct.length > 0 ? (
              <ul aria-label="Product checks owed" className="m-0 mt-(--s3) flex list-none flex-wrap gap-(--s2) p-0">
                {owedProduct.map((o) => (
                  <OwedChip key={o.obligationId} o={o} />
                ))}
              </ul>
            ) : null}

            {/* The status read's failure is said above, in the counts' place. This is the OWED
                list's: without it the chips are simply absent, which must not read as "nothing
                owed" (rule 15). */}
            {obligations.error ? (
              <div role="alert" className="mt-(--s2) flex flex-wrap items-center gap-(--s2) text-(length:--fs-sm)">
                <span className="font-semibold text-(--warn)">Owed checks not known: {obligations.error}</span>
                <Button type="button" variant="outline" size="sm" onClick={obligations.reload}>
                  Try again
                </Button>
              </div>
            ) : null}
          </div>

          {/* No search box on this table, deliberately: it would be the filter
              the header of this file forbids, the one that lets a refusal go
              unseen. A refused reading carries the red bar however it is sorted. */}
          <DataTable
            key={selected}
            data={reads}
            columns={readingCols}
            caption="Every reading on this run, accepted and rejected"
            getRowId={(r) => String(r.id)}
            rowAccent={(r) => (r.ingestVerdict === 'REJECTED' ? 'bad' : null)}
            stickyFirst
            pageSize={50}
            loading={readings.loading}
            // UX-10 (UI/UX audit, 31 Oct 2026; rule 15): the failure goes to the table, which shows
            // it in the rows' place with Try again. As a separate alert below, the table above it
            // still said "No readings on this run yet." - a claim the failed read never made.
            error={readings.error}
            onRetry={readings.reload}
            empty="No readings on this run yet."
          />
        </section>
      ) : null}
    </div>
  );
}

export default Runs;
